// Angular modules.
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
// Third-party modules.
import { TranslateService } from '@ngx-translate/core';
import { ZkNotificationService } from '@zenklub/ng-ui';
// Zenklub interfaces.
import { IContact, IContactStatus } from 'app/interfaces/contact.interface';
import { IUserPreferences } from 'app/interfaces/userpreferences.interface';
// Zenklub services.
import { ContactsService } from 'app/services/contacts.service';
import { CurrencyService } from 'app/services/currency.service';
import { GlobalService } from 'app/services/global.service';
import { LocationService } from 'app/services/location.service';
import { UserService } from 'app/services/user.service';
import { enterAnimation } from 'app/utils/animations.util';
import * as $ from 'jquery';
import { MessageService } from 'primeng/api';

@Component({
	selector: 'app-clientsedit',
	templateUrl: './clients-edit.component.html',
	styleUrls: ['./clients-edit.component.scss'],
	providers: [MessageService],
	animations: [enterAnimation],
})
export class ClientsEditComponent implements OnInit {
	// Outputs.
	@Output() cancelEditClient: EventEmitter<any> = new EventEmitter<any>();

	// User data.
	clientId: number;
	clientData: IContact;
	userPref: IUserPreferences;
	statusList: IContactStatus[] = [];
	sessionPrice: number;
	sessionPriceHalfHour: number;

	// Status.
	isLoading = false;
	isMobile = false;
	isRedirecting = false;
	pageTitle = '';

	// Constructor.
	constructor(
		protected currency: CurrencyService,
		protected contact: ContactsService,
		protected global: GlobalService,
		protected notifications: ZkNotificationService,
		protected location: LocationService,
		protected route: ActivatedRoute,
		protected router: Router,
		public translate: TranslateService,
		protected user: UserService
	) {
		this.pageTitle = this.translate.instant('PAGES.CLIENTS.EDITCLIENT');
		this.router.events.subscribe((event) => {
			if (event.constructor.name === 'NavigationStart' && !this.isRedirecting) {
				const ev: any = event;
				const url: string = ev.url;
				if (url.indexOf('/clients/edit') === -1) {
					this.isRedirecting = true;
					this.cancelEditClient.emit();
				}
			}
		});
	}

	// On Init.
	public ngOnInit(): void {
		// Loader.
		this.isLoading = true;

		// Verify if is a mobile device.
		this.isMobile = this.global.isMobileDevice();

		// Initialize object.
		this.resetContactData();

		// Get user preferences.
		this.userPref = this.user.getUserLocalPreferences();

		// Store result and set default.
		this.statusList = this.contact.getStatusList();
		this.clientData.status = this.statusList[0].key;

		// Get user ID.
		this.route.firstChild.params.subscribe((params) => {
			if (params['id']) {
				// Store user ID.
				this.clientId = params['id'];
				// Get user data.
				this.contact.getContact(this.clientId).subscribe((r) => {
					// Store user data.
					this.clientData = r;
					this.clientData.notes =
						this.clientData.notes == null ? '' : this.clientData.notes;
					// Get formated session price.
					this.sessionPrice = this.clientData.sessionPrice;
					this.sessionPriceHalfHour = this.clientData.sessionPriceHalfHour;
					// Loader.
					this.isLoading = false;
				});
			}
		});
	}

	// Reset data object.
	public resetContactData(): void {
		this.clientData = {} as IContact;
	}

	// Cancel add new.
	public cancel(force = false): void {
		if (force || confirm(this.translate.instant('MESSAGES.AREYOUSURE'))) {
			this.resetContactData();
			this.redirectPage();
		}
	}

	// Save existing contact.
	public save(): void {
		this.isLoading = true;
		// Update clientData sessionPrice property if session price var has value
		const oldValue = this.clientData.sessionPrice;
		const oldValueHalfHour = this.clientData.sessionPriceHalfHour;
		this.clientData.sessionPrice = this.sessionPrice;
		this.clientData.sessionPriceHalfHour = this.sessionPriceHalfHour;

		// Validate form.
		if (this.validate(this.clientData)) {
			// Try to save.
			this.contact.updateContact(this.clientData).subscribe(
				() => {
					// update local contacts
					this.notifications.showSuccess(
						this.translate.instant('PAGES.CLIENTS.UPDATED_DATA')
					);
					this.isLoading = false;
					// Go to list page.
					this.cancel(true);
				},
				({ error }) => {
					this.sessionPrice = oldValue;
					this.clientData.sessionPrice = oldValue;
					this.sessionPriceHalfHour = oldValueHalfHour;
					this.clientData.sessionPriceHalfHour = oldValueHalfHour;
					this.isLoading = false;
					this.notifications.showError(
						error.messageI18n,
						this.translate.instant('MESSAGES.SAVEDERROR')
					);
				}
			);
		} else {
			this.isLoading = false;
		}
	}
	// Validate data.
	public validate(contact: IContact): boolean {
		const errors: string[] = [];
		if (!contact.displayName) {
			errors.push(
				this.translate.instant('ERRORS.REQUIRED', {
					value: this.translate.instant('GENERAL.NAME'),
				})
			);
		}
		if (!contact.sessionPrice && contact.sessionPrice === 0) {
			errors.push(
				this.translate.instant('ERRORS.MIN_SESSIONPRICE', {
					value: this.translate.instant('GENERAL.PRICEPERSESSION'),
				})
			);
		}
		if (!contact.sessionPriceHalfHour && contact.sessionPriceHalfHour === 0) {
			errors.push(
				this.translate.instant('ERRORS.MIN_SESSIONPRICE', {
					value: this.translate.instant('GENERAL.PRICEPERSESSION_HALF_HOUR'),
				})
			);
		}
		if (errors.length > 0) {
			this.notifications.showError(errors.join('<br/>'), this.pageTitle);
			return false;
		}
		return true;
	}

	// On page resize.
	public onResize(): void {
		// Verify if is a mobile device.
		this.isMobile = this.global.isMobileDevice();
		let callAgain = false;

		// Birthday.
		const inpBirthday: JQuery<HTMLElement> = $('#inpBirthday');
		if (inpBirthday && inpBirthday.length > 0) {
			inpBirthday.css('max-width', '300px');
			$('.owl-dateTime-cancel').hide();
			const faCal: JQuery<HTMLElement> = $('.iconCal');
			faCal.css('margin-top', '-32px');
			faCal.css('position', 'absolute');
			faCal.css('margin-left', inpBirthday.width());
		} else {
			callAgain = true;
		}
		if (callAgain) {
			setTimeout(() => {
				this.onResize();
			}, 100);
		}
	}

	private redirectPage(): void {
		if (history.state.returnTo) {
			this.isRedirecting = true;
			this.router.navigate([history.state.returnTo]);
			return;
		}

		this.cancelEditClient.emit();
	}
}
